import React from 'react';
import { useTheme } from '@emotion/react';
import { Box, CircularProgress, Grid, Typography, useMediaQuery } from '@mui/material';
import { ArrowDownwardRounded, ArrowUpwardRounded } from '@mui/icons-material';
import { useToast } from 'context/ToastContext';
import CustomInputField from 'ui-components/CustomInputField';
import { FetchAdminConfig, FetchAdminConfigRevisions, PreviewProfileWeights, RestoreAdminConfigRevision, UpdateAdminConfig } from 'api/admin_configs';
import Section from './plans/Section';
import SaveBar from './plans/SaveBar';
import ConfirmDialog from './plans/ConfirmDialog';
import HistoryPanel, { HistoryButton } from './plans/HistoryPanel';

const TYPE = 'profile_completion';
const TOTAL = 100;

const describeRevision = (paths = []) => {
    const sections = paths.filter((p) => p.startsWith('weights.')).map((p) => p.split('.')[1]);
    return sections.length ? `Changed: ${sections.join(', ').replace(/_/g, ' ')}` : 'No weights changed';
};

/**
 * Profile Score: how much each part of a startup's profile adds to its completion %.
 * Weights must total 100. Before saving, the preview shows what changes for every
 * startup (and who crosses the 50% investor-invite line); saving recalculates them all.
 */
const ProfileScore = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { addToast } = useToast();
    const c = theme.palette.customColors;

    const { data: config, isLoading, refetch } = FetchAdminConfig(TYPE);
    const { mutateAsync: saveConfig, isLoading: isSaving } = UpdateAdminConfig(TYPE);
    const { mutateAsync: restoreRevision, isLoading: isRestoring } = RestoreAdminConfigRevision(TYPE);
    const { mutateAsync: previewAsync } = PreviewProfileWeights();
    const [historyOpen, setHistoryOpen] = React.useState(false);
    const { data: revisions, isLoading: loadingRevisions, refetch: refetchRevisions } = FetchAdminConfigRevisions(TYPE, { enabled: historyOpen });

    const [draft, setDraft] = React.useState(null); // { weights, updated_at }
    const [baseline, setBaseline] = React.useState(null);
    const [impact, setImpact] = React.useState(null);
    const [loadingImpact, setLoadingImpact] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [serverProblems, setServerProblems] = React.useState([]);

    const sections = config?.meta?.sections || [];

    const load = React.useCallback((saved) => {
        const next = { weights: { ...(saved?.data?.weights || {}) }, updated_at: saved?.updated_at || null };
        setDraft(next);
        setBaseline(JSON.stringify(next.weights));
        setImpact(null);
        setServerProblems([]);
    }, []);

    // Loaded once; a background refetch must not overwrite edits in progress.
    React.useEffect(() => { if (config && !draft) load(config); }, [config, draft, load]);

    const weights = draft?.weights || {};
    const total = sections.reduce((sum, s) => sum + (Number.isInteger(weights[s.key]) ? weights[s.key] : 0), 0);
    const dirty = Boolean(draft) && JSON.stringify(weights) !== baseline;
    const invalidKeys = sections.filter((s) => !Number.isInteger(weights[s.key]) || weights[s.key] < 0 || weights[s.key] > TOTAL).map((s) => s.key);
    const problems = [
        ...invalidKeys.map((key) => ({ path: key, message: `${sections.find((s) => s.key === key)?.label}: enter a whole number from 0 to ${TOTAL}` })),
        ...(invalidKeys.length === 0 && total !== TOTAL ? [{ path: 'total', message: `Weights add up to ${total}. They must add up to exactly ${TOTAL}.` }] : []),
        ...serverProblems,
    ];
    const valid = invalidKeys.length === 0 && total === TOTAL;

    // Impact preview, refreshed shortly after the weights settle on a valid, changed set.
    React.useEffect(() => {
        if (!dirty || !valid) { setImpact(null); return undefined; }
        const timer = setTimeout(async () => {
            setLoadingImpact(true);
            try {
                const response = await previewAsync(weights);
                setImpact(response?.data?.data || null);
            } catch (error) {
                setImpact(null);
            } finally {
                setLoadingImpact(false);
            }
        }, 500);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(weights), dirty, valid]);

    const setWeight = (key, raw) => {
        const value = raw === '' ? '' : Number(raw);
        setServerProblems([]);
        setDraft({ ...draft, weights: { ...weights, [key]: Number.isNaN(value) ? '' : value } });
    };

    const handleSave = async () => {
        setConfirmOpen(false);
        try {
            const response = await saveConfig({ data: { weights }, updated_at: draft.updated_at });
            const saved = response?.data?.data;
            load(saved);
            const applied = saved?.applied;
            addToast(`Saved. ${applied ? `${applied.updated} of ${applied.total} startups' scores updated.` : ''}`, 'success');
            refetch();
            refetchRevisions();
        } catch (error) {
            const body = error?.response?.data;
            const details = body?.message?.errors || body?.errors;
            if (Array.isArray(details) && details.length) setServerProblems(details.map((message) => ({ path: 'server', message })));
            else addToast(body?.message || 'Error: Something went wrong.', 'error');
        }
    };

    if (isLoading || !draft) {
        return (
            <Grid item xs={12} paddingInline={isSmallScreen ? '1rem' : '2rem'}>
                <Box className='skeleton' minHeight={"60dvh"} sx={{ borderRadius: '8px', border: `1px solid ${c.b50}` }} />
            </Grid>
        );
    }

    const published = JSON.parse(baseline || '{}');
    const stat = (label, value, color) => (
        <Box sx={{ padding: '0.75rem 1rem', borderRadius: '8px', border: `1px solid ${c.b50}`, minWidth: '9rem' }}>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: color || c.g300 }}>{value}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: c.g200 }}>{label}</Typography>
        </Box>
    );

    return (
        <Grid container paddingInline={isSmallScreen ? '1rem' : '2rem'} display="grid" gap="1rem">
            <Section
                title="Profile score weights"
                description={`How much each part of a startup's profile adds to its completion %. They must add up to ${TOTAL}. Startups that already reached 50% keep investor invites if their % drops, and see a note on their profile explaining the change.`}
                action={<HistoryButton onClick={() => setHistoryOpen(true)} />}
            >
                <Box sx={{ display: 'grid', gap: '0.75rem' }}>
                    {sections.map((section) => {
                        const value = weights[section.key];
                        const changed = value !== published[section.key];
                        return (
                            <Box key={section.key} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 6rem', sm: '1fr 7rem 10rem' }, alignItems: 'center', gap: '1rem' }}>
                                <Box>
                                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: c.g300 }}>{section.label}</Typography>
                                    {changed && (
                                        <Typography sx={{ fontSize: '0.75rem', color: c.b300 }}>was {published[section.key]}%</Typography>
                                    )}
                                </Box>
                                <CustomInputField
                                    value={value}
                                    onChange={(event) => setWeight(section.key, event.target.value.replace(/[^0-9]/g, ''))}
                                    inputProps={{ inputMode: 'numeric', 'aria-label': `${section.label} weight`, maxLength: 3 }}
                                    hasError={invalidKeys.includes(section.key)}
                                    sx={{ marginBottom: 0 }}
                                    endAdornment={<Typography sx={{ color: c.g200, paddingRight: '0.5rem' }}>%</Typography>}
                                />
                                <Box sx={{ display: { xs: 'none', sm: 'block' }, height: 8, borderRadius: 4, backgroundColor: c.b50, overflow: 'hidden' }}>
                                    <Box sx={{ height: '100%', width: `${Math.min(Number(value) || 0, TOTAL)}%`, backgroundColor: c.b200 }} />
                                </Box>
                            </Box>
                        );
                    })}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${c.b50}`, paddingTop: '0.75rem' }}>
                        <Typography sx={{ fontWeight: 700, color: c.g300 }}>Total</Typography>
                        <Typography sx={{ fontWeight: 700, color: total === TOTAL ? c.g300 : c.r500 }}>{total} / {TOTAL}</Typography>
                    </Box>
                </Box>
            </Section>

            {dirty && valid && (
                <Section title="Impact if you save" description="Every startup re-scored with these weights, compared with today's weights.">
                    {loadingImpact || !impact ? (
                        <Box display="flex" justifyContent="center" padding="1rem"><CircularProgress size="1.5rem" /></Box>
                    ) : (
                        <Box sx={{ display: 'grid', gap: '1rem' }}>
                            <Box sx={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                {stat('startups go up', impact.up)}
                                {stat('startups go down', impact.down)}
                                {stat(`fall below ${impact.gate}% (keep invites, grandfathered)`, impact.fall_below_gate)}
                                {stat(`gain investor invites (reach ${impact.gate}%)`, impact.gain_invites)}
                                {stat('saved scores also corrected (out of date)', impact.stored_out_of_date)}
                            </Box>
                            {impact.biggest_changes?.length > 0 && (
                                <Box>
                                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: c.g500, marginBottom: '0.5rem' }}>Biggest changes</Typography>
                                    {impact.biggest_changes.map((row, index) => (
                                        <Box key={`${row.name}-${index}`} sx={{ display: 'flex', justifyContent: 'space-between', padding: '0.375rem 0', borderBottom: `1px solid ${c.b50}` }}>
                                            <Typography sx={{ fontSize: '0.8125rem', color: c.g300 }}>{row.name}</Typography>
                                            <Typography sx={{ fontSize: '0.8125rem', color: row.after < row.before ? c.r500 : c.g300, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                                {row.before}% → {row.after}%
                                                {row.after < row.before ? <ArrowDownwardRounded sx={{ fontSize: 14 }} /> : <ArrowUpwardRounded sx={{ fontSize: 14 }} />}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}
                </Section>
            )}

            <SaveBar
                dirty={dirty}
                saving={isSaving}
                summary="Profile score weights changed"
                problems={dirty ? problems : serverProblems}
                target="recalculates every startup's score"
                saveLabel="Save & apply"
                onSave={() => valid && setConfirmOpen(true)}
                onDiscard={() => load({ data: { weights: published }, updated_at: draft.updated_at })}
            />

            <ConfirmDialog
                open={confirmOpen}
                title="Apply new profile score weights?"
                body={impact
                    ? `${impact.up} startups go up and ${impact.down} go down (they see a note explaining why). ${impact.fall_below_gate} fall below ${impact.gate}% but keep investor invites; ${impact.gain_invites} gain them. Every startup's saved score is recalculated now.`
                    : "Every startup's saved score is recalculated now."}
                confirmLabel="Save & apply"
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleSave}
            />

            <HistoryPanel
                open={historyOpen}
                handleClose={() => setHistoryOpen(false)}
                revisions={revisions || []}
                loading={loadingRevisions}
                restoring={isRestoring}
                describeRevision={describeRevision}
                restoreWhat="Profile score weights (and every startup's score)"
                onRestore={async (revision) => {
                    try {
                        const response = await restoreRevision({ _id: revision._id });
                        load(response?.data?.data);
                        setHistoryOpen(false);
                        addToast('Restored, and every startup\'s score recalculated.', 'success');
                        refetch();
                        refetchRevisions();
                    } catch (error) {
                        addToast(error?.response?.data?.message || 'Could not restore that version.', 'error');
                    }
                }}
            />
        </Grid>
    );
};

export default ProfileScore;
